import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import logoFull from "@/assets/logo.png";
import logoIcon from "@/assets/logo-icon.png";

interface SignupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultEmail?: string;
  refId?: string | null;
}

const detectedOffset = () => {
  const offsetMin = new Date().getTimezoneOffset();
  return (-offsetMin / 60).toString();
};

const STEPS = [
  { title: "Let's get you started", description: "Your email is your login. Pick a password you'll remember." },
  { title: "Where in the world are you?", description: "We'll use this to deliver your edition at the right local time." },
  { title: "When should we deliver?", description: "Pick the hour that fits your morning routine. You can always change this later." },
];

const SignupModal = ({ open, onOpenChange, defaultEmail = "", refId }: SignupModalProps) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState("");
  const [autoDetectTz, setAutoDetectTz] = useState(true);
  const [gmtOffset, setGmtOffset] = useState(detectedOffset);
  const [deliveryTime, setDeliveryTime] = useState("8");
  const [timeFormat, setTimeFormat] = useState<"12h" | "24h">("24h");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Track referral click
  useEffect(() => {
    if (refId) {
      supabase.from("referral_clicks").insert({ referrer_id: refId }).then(() => {});
    }
  }, [refId]);

  useEffect(() => {
    if (!open) return;
    setEmail(defaultEmail);
    setPassword("");
    setStep(1);
    setAutoDetectTz(true);
    setGmtOffset(detectedOffset());
  }, [defaultEmail, open]);

  useEffect(() => {
    if (autoDetectTz) setGmtOffset(detectedOffset());
  }, [autoDetectTz]);

  const canAdvanceStep1 = email.trim() !== "" && password.length >= 6;

  const handleSubmit = async () => {
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: "https://aibly.club",
        data: {
          delivery_time: parseInt(deliveryTime),
          timezone: `GMT${parseFloat(gmtOffset) >= 0 ? "+" : ""}${gmtOffset}`,
        },
      },
    });

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else if (data.user && data.user.identities && data.user.identities.length === 0) {
      toast({
        title: "Email already registered",
        description: "This email is already in use.",
        variant: "destructive",
        action: (
          <Button
            variant="outline"
            size="sm"
            className="rounded-none text-xs"
            onClick={() => { onOpenChange(false); navigate("/login"); }}
          >
            Login
          </Button>
        ),
      });
    } else {
      if (refId && data.user) {
        await supabase.from("referrals").insert({
          referrer_id: refId,
          referred_email: email,
          status: "pending",
        });
      }
      onOpenChange(false);
      navigate("/confirm-email");
    }
    setLoading(false);
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const currentStep = STEPS[step - 1];
  const progress = (step / 3) * 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm rounded-none border-border bg-background p-0 overflow-hidden">
        <Progress value={progress} className="h-1 rounded-none" />

        <div className="p-6 pt-4">
          <DialogHeader className="space-y-2 text-center">
            <div className="flex items-center justify-center">
              <img src={logoIcon} alt="aibly logo" width={500} height={500} className="h-16 w-auto dark:invert sm:hidden" />
              <img src={logoFull} alt="aibly logo" width={486} height={135} className="h-10 w-auto dark:invert hidden sm:block" />
            </div>
            <DialogTitle className="font-display text-xl italic text-foreground">
              {currentStep.title}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {currentStep.description}
            </DialogDescription>
            <p className="text-[10px] tracking-widest uppercase text-muted-foreground">
              Step {step} of 3
            </p>
          </DialogHeader>

          {/* Step 1 — Account */}
          <div className={`space-y-4 mt-4 transition-opacity duration-200 ${step === 1 ? "opacity-100" : "hidden"}`}>
            <Input
              aria-label="Email address"
              type="email"
              placeholder="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="rounded-none bg-secondary border-border text-foreground placeholder:text-muted-foreground text-sm"
            />
            <Input
              aria-label="Password"
              type="password"
              placeholder="password (min 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="rounded-none bg-secondary border-border text-foreground placeholder:text-muted-foreground text-sm"
            />
            <Button
              type="button"
              disabled={!canAdvanceStep1}
              onClick={() => setStep(2)}
              className="w-full rounded-none text-xs tracking-widest uppercase"
            >
              Next
            </Button>
          </div>

          {/* Step 2 — Timezone */}
          <div className={`space-y-4 mt-4 transition-opacity duration-200 ${step === 2 ? "opacity-100" : "hidden"}`}>
            <div className="flex items-center justify-between">
              <label className="text-xs text-muted-foreground">Auto-detect timezone</label>
              <Switch aria-label="Auto-detect timezone" checked={autoDetectTz} onCheckedChange={setAutoDetectTz} />
            </div>

            <div className="space-y-1">
              <label className="text-xs tracking-widest uppercase text-muted-foreground">
                Timezone
              </label>
              <Select value={gmtOffset} onValueChange={setGmtOffset} disabled={autoDetectTz}>
                <SelectTrigger className="rounded-none bg-secondary border-border text-foreground text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border max-h-60">
                  {Array.from({ length: 25 }, (_, i) => i - 12).map((offset) => (
                    <SelectItem key={offset} value={offset.toString()} className="text-xs">
                      GMT{offset >= 0 ? "+" : ""}{offset}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                className="rounded-none text-xs px-3"
              >
                <ArrowLeft className="h-3 w-3 mr-1" /> Back
              </Button>
              <Button
                type="button"
                onClick={() => setStep(3)}
                className="flex-1 rounded-none text-xs tracking-widest uppercase"
              >
                Next
              </Button>
            </div>
          </div>

          {/* Step 3 — Delivery Time */}
          <div className={`space-y-4 mt-4 transition-opacity duration-200 ${step === 3 ? "opacity-100" : "hidden"}`}>
            <div className="space-y-1">
              <label className="text-xs tracking-widest uppercase text-muted-foreground">
                Deliver at
              </label>
              <div className="flex gap-2">
                <Select value={deliveryTime} onValueChange={setDeliveryTime}>
                  <SelectTrigger className="rounded-none bg-secondary border-border text-foreground flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {hours.map((h) => (
                      <SelectItem key={h} value={h.toString()}>
                        {timeFormat === "12h"
                          ? `${h === 0 ? 12 : h > 12 ? h - 12 : h}:00 ${h < 12 ? "AM" : "PM"}`
                          : `${h.toString().padStart(2, "0")}:00`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex">
                  <Button
                    type="button"
                    size="sm"
                    variant={timeFormat === "24h" ? "default" : "outline"}
                    onClick={() => setTimeFormat("24h")}
                    className="rounded-none text-xs px-3"
                  >
                    24h
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={timeFormat === "12h" ? "default" : "outline"}
                    onClick={() => setTimeFormat("12h")}
                    className="rounded-none text-xs px-3"
                  >
                    12h
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(2)}
                className="rounded-none text-xs px-3"
              >
                <ArrowLeft className="h-3 w-3 mr-1" /> Back
              </Button>
              <Button
                type="button"
                disabled={loading}
                onClick={handleSubmit}
                className="flex-1 rounded-none text-xs tracking-widest uppercase"
              >
                {loading ? "Creating account..." : "Subscribe — Free"}
              </Button>
            </div>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-4">
            Already subscribed?{" "}
            <button
              type="button"
              onClick={() => { onOpenChange(false); navigate("/login"); }}
              className="text-foreground hover:text-accent transition-colors"
            >
              Login
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignupModal;
